import {
  Paper,
  Stack,
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  IconButton
} from '@mui/material';
import { useState, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatearFechaLocal, normalizarFechaISO } from '../../utils/horaLocal';

export default function TraspasoCaja({ state }) {
  const [openTraspaso, setOpenTraspaso] = useState(false);
  const [monto, setMonto] = useState('');
  const [direccion, setDireccion] = useState('CAJ_A_QR'); // CAJ_A_QR o QR_A_CAJ
  const [razon, setRazon] = useState('');

  // Verificar si el día está bloqueado por arqueo
  const díaBloqueado = state?.verificarDíaBloqueado();
  const arqueoDelDía = state?.obtenerArqueoDelDía();

  const traspasosQR = state.traspasosQR || [];

  const resumen = useMemo(() => {
    const activos = traspasosQR.filter(t => !t.anulado);
    const cajaAQR = activos
      .filter(t => t.direccion === 'CAJ_A_QR')
      .reduce((a, b) => a + b.monto, 0);
    const qRACaja = activos
      .filter(t => t.direccion === 'QR_A_CAJ')
      .reduce((a, b) => a + b.monto, 0);

    return { cajaAQR, qRACaja, neto: qRACaja - cajaAQR };
  }, [traspasosQR]);

  const formatearMonto = (monto) => {
    return new Intl.NumberFormat('es-BO', {
      style: 'currency',
      currency: 'BOB'
    }).format(monto);
  };

  const handleAgregarTraspaso = async () => {
    if (!monto || isNaN(monto) || parseFloat(monto) <= 0) {
      alert("Ingresa un monto válido");
      return;
    }

    await state.traspasoQRCaja(parseFloat(monto), direccion);
    
    setOpenTraspaso(false);
    setMonto('');
    setRazon('');
    setDireccion('CAJ_A_QR');
  };

  const handleAnularTraspaso = async (id) => {
    if (!window.confirm("¿Anular este traspaso?")) return;
    await state.anularTraspaso(id);
  };

  return (
    <Stack spacing={3}>
      {/* RESUMEN */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Trasladado a QR
            </Typography>
            <Typography variant="h5" sx={{ color: '#2196f3', fontWeight: 'bold' }}>
              {formatearMonto(resumen.cajaAQR)}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Trasladado a Caja
            </Typography>
            <Typography variant="h5" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
              {formatearMonto(resumen.qRACaja)}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <CardContent>
            <Typography color="white" gutterBottom>
              Balance Neto
            </Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
              {formatearMonto(resumen.neto)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {resumen.neto > 0 ? 'QR debe a Caja' : 'Caja debe a QR'}
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* ADVERTENCIA DE DÍA BLOQUEADO */}
      {díaBloqueado && (
        <Box sx={{ 
          bgcolor: '#ffebee', 
          p: 2, 
          borderRadius: 2, 
          border: '2px solid #f44336',
          mb: 3
        }}>
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 'bold', color: '#d32f2f', fontSize: '1.1rem' }}>
              🔒 DÍA BLOQUEADO - ARQUEO DE CAJA COMPLETADO
            </Typography>
            <Typography variant="body2" sx={{ color: '#c62828' }}>
              No puedes crear traspasos. El arqueo de caja fue completado hoy a las {new Date(arqueoDelDía.fecha).toLocaleTimeString('es-BO')}.
            </Typography>
            <Typography variant="body2" sx={{ color: '#c62828', fontStyle: 'italic' }}>
              Para desbloquear, ve a la pestaña "Arqueo de Caja" y anula el arqueo de hoy.
            </Typography>
          </Stack>
        </Box>
      )}

      {/* BOTÓN AGREGAR TRASPASO */}
      <Button
        variant="contained"
        color="primary"
        onClick={() => setOpenTraspaso(true)}
        disabled={díaBloqueado}
      >
        + Nuevo Traspaso
      </Button>

      {/* TABLA DE TRASPASOSPAS */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Historial de Traspasos
        </Typography>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                <TableCell>Fecha</TableCell>
                <TableCell>Desde</TableCell>
                <TableCell>Hacia</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell align="center">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {traspasosQR.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="textSecondary">
                      Sin traspasos registrados
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                traspasosQR.map(t => (
                  <TableRow key={t.id} sx={{ opacity: t.anulado ? 0.4 : 1 }}>
                    <TableCell>{formatearFechaLocal(normalizarFechaISO(t.fecha))}</TableCell>
                    <TableCell>
                      <Chip
                        label={t.direccion === 'CAJ_A_QR' ? 'CAJA' : 'QR'}
                        size="small"
                        color={t.direccion === 'CAJ_A_QR' ? 'default' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.direccion === 'CAJ_A_QR' ? 'QR' : 'CAJA'}
                        size="small"
                        color={t.direccion === 'CAJ_A_QR' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right" fontWeight="bold">
                      {formatearMonto(t.monto)}
                    </TableCell>
                    <TableCell align="center">
                      {!t.anulado && (
                        <IconButton
                          size="small"
                          color="error"
                          disabled={díaBloqueado}
                          onClick={() => handleAnularTraspaso(t.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* MODAL DE NUEVO TRASPASO */}
      <Dialog open={openTraspaso} onClose={() => setOpenTraspaso(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nuevo Traspaso de Fondos</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={3}>
            <Typography variant="body2" color="textSecondary">
              Registra movimientos de efectivo entre caja y QR
            </Typography>

            {/* SELECTOR DIRECCIÓN */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                ¿De dónde a dónde?
              </Typography>
              <ToggleButtonGroup
                value={direccion}
                exclusive
                onChange={(e, val) => val && setDireccion(val)}
                fullWidth
              >
                <ToggleButton value="CAJ_A_QR">Caja → QR</ToggleButton>
                <ToggleButton value="QR_A_CAJ">QR → Caja</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* MONTO */}
            <TextField
              label="Monto"
              type="number"
              fullWidth
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              inputProps={{ step: '0.01', min: '0' }}
              autoFocus
            />

            {/* RAZÓN (OPCIONAL) */}
            <TextField
              label="Nota (opcional)"
              fullWidth
              multiline
              rows={2}
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              placeholder="Ej: Depósito bancario, retiro de efectivo, etc."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTraspaso(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAgregarTraspaso}
          >
            Registrar Traspaso
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
